<template>
  <div>
    <Label v-if="label" :class="{ 'ml-3': async }" :required="required">
      {{ label }}
      <template #additional-icon>
        <slot name="additional-icon" />
      </template>
    </Label>
    <div
      class="group min-h-[36px] rounded-md bg-white ring-1 ring-inset"
      :class="[
        async ? 'ring-transparent' : 'bg-white py-1.5 shadow-sm ring-gray-300',
        computedDisabled
          ? async
            ? ''
            : 'cursor-not-allowed !bg-gray-50 text-gray-500 !ring-gray-200'
          : async
            ? 'cursor-text hover:bg-gray-50 hover:ring-gray-300'
            : 'cursor-text text-gray-900',
        inputVisible
          ? 'border-0 bg-gray-50 py-1.5 text-sm leading-none shadow-sm outline-0 ring-2 ring-inset !ring-indigo-600'
          : '',
      ]"
      @click="showInput"
    >
      <Combobox v-model="selectedIds" as="div" multiple class="size-full">
        <div
          ref="comboboxOptionsPositionRef"
          :class="{ 'pr-9': isDropDownArrowVisible }"
          class="relative"
        >
          <ComboboxButton
            v-if="isDropDownArrowVisible"
            class="absolute inset-y-0 right-0 items-center rounded-r-md px-2 focus:outline-none"
            :class="[
              async
                ? inputVisible || isLoading
                  ? 'flex'
                  : 'hidden disabled:!hidden group-hover:flex'
                : 'flex disabled:cursor-not-allowed',
              isLoading ? 'cursor-progress' : '',
            ]"
            :disabled="computedDisabled"
          >
            <ChevronUpDownIcon
              v-if="!isLoading"
              class="size-5 text-gray-400"
              aria-hidden="true"
            />
            <Spinner v-else colored />
          </ComboboxButton>

          <div class="flex flex-wrap px-1.5">
            <TagsInputTag
              v-for="(selectedOption, index) in selectedOptions"
              :key="string ? selectedOption : selectedOption[idKey]"
              :label="string ? selectedOption : selectedOption[filterKey]"
              :tag-color="
                individualTagColor
                  ? selectedOption[individualTagColor]
                  : tagColor
              "
              class="m-1"
              :read-only="(!inputVisible && async) || computedDisabled"
              @remove-option="removeOption(index)"
            />
          </div>

          <div
            v-if="!selectedOptions.length || inputVisible"
            class="relative flex items-center"
          >
            <ComboboxInput
              ref="inputRef"
              class="relative my-1 h-7 w-full grow whitespace-pre-wrap border-0 bg-transparent text-gray-900 placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 sm:text-sm"
              :class="{ 'cursor-not-allowed': computedDisabled && !async }"
              :placeholder="computedPlaceholder"
              :disabled="
                computedDisabled ||
                  (!innerOptions?.length && !string && !autoCompleteCollection)
              "
              autocomplete="off"
              @change="query = $event.target.value"
              @keyup.enter="handleInputByKeyUpEvent"
            />
            <kbd
              v-if="hasVariableOptions && query.length"
              class="pointer-events-none absolute right-3 rounded border border-gray-200 bg-white px-2 py-1 shadow-sm"
            >
              <svg class="size-4 fill-gray-400" viewBox="0 0 512 512">
                <path
                  d="M448 64c0-17.7 14.3-32 32-32s32 14.3 32 32l0 160c0 53-43 96-96 96l-306.7 0 73.4 73.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3l128-128c12.5-12.5 32.8-12.5 45.3 0s12.5 32.8 0 45.3L109.3 256 416 256c17.7 0 32-14.3 32-32l0-160z"
                />
              </svg>
            </kbd>
          </div>
        </div>
        <teleport to="body">
          <ComboboxOptions
            v-if="inputVisible && !computedDisabled && !hasVariableOptions"
            ref="comboboxOptionsRef"
            class="z-60 absolute left-0 my-2 hidden max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
            static
          >
            <div
              v-if="!filteredOptions.length"
              class="my-4 flex w-full items-center justify-center"
            >
              <Spinner v-if="isAutoCompleteSpinnerLoading" colored />
              <span v-else class="block px-2 text-center text-gray-500">
                {{ $t('general.no-options-found') }}
              </span>
            </div>
            <template v-else>
              <ComboboxOption
                v-for="(option, index) in filteredOptions"
                :key="string ? option : option[idKey]"
                v-slot="{ active, selected }"
                as="template"
                :value="string ? option : option[idKey]"
                :class="hasDivider(index) ? 'border-b border-gray-200' : ''"
                @click.stop
              >
                <li
                  :class="[
                    'relative cursor-pointer select-none py-2 pl-3 pr-9',
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                  ]"
                >
                  <span
                    :class="['block truncate', selected && 'font-semibold']"
                  >
                    {{ string ? option : option[filterKey] }}
                  </span>

                  <span
                    v-if="selected"
                    :class="[
                      'absolute inset-y-0 right-0 flex items-center pr-4',
                      active ? 'text-white' : 'text-indigo-600',
                    ]"
                  >
                    <CheckIcon class="size-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </template>
            <div
              v-if="!!$slots.footer"
              class="border-t-2 border-solid border-gray-300"
            >
              <slot name="footer" />
            </div>
          </ComboboxOptions>
        </teleport>
      </Combobox>
    </div>
  </div>
</template>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/7864450/TagsInput

import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import type { Resolver } from '@trpc/client';
import { isEqual, debounce } from 'lodash';
import { klona } from 'klona';
import { flip, offset } from '@floating-ui/dom';
import { useOutsideClick } from '@/util/use-outside-click';
import type { DATABASE_AUTOCOMPLETE_COLLECTION } from '@/types';
import { useNotifications } from '~/stores/notifications';
import { useSmartPositioning } from '~/composables/smart-positioning';
/* eslint-disable @typescript-eslint/no-explicit-any */

const props = defineProps({
  modelValue: {
    type: Array as PropType<any[]>,
    required: true,
  },
  options: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  idKey: {
    type: String,
    default: '_id',
  },
  filterKey: {
    type: String,
    default: 'name',
  },
  string: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  queryEndpoint: {
    type: Function as PropType<Resolver<any>>,
    default: undefined,
  },
  queryParams: {
    type: Object,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tagColor: {
    type: String,
    default: 'indigo',
  },
  individualTagColor: {
    type: String,
    default: undefined,
  },
  async: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  autoCompleteCollection: {
    type: String as PropType<DATABASE_AUTOCOMPLETE_COLLECTION>,
  },
  required: {
    type: Boolean,
  },
  divideAfterIndexes: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  hasDividerForLastOption: {
    type: Boolean,
    default: false,
  },
  preselectedOptionIds: {
    // use only in combination with autoCompleteCollection or props.options
    type: Array as PropType<string[]>,
  },
  preselectedOptions: {
    // If you want to combine preselectedOptionIds with preselectedOptions
    type: Array as PropType<any[]>,
  },
  // Filter out the blacklisted options
  blacklist: {
    type: Array as PropType<{ _id: string }[]>,
  },
});

const emit = defineEmits(['update:modelValue', 'optionsFetched']);

const { $client } = useNuxtApp();
const notificationsStore = useNotifications();
const i18n = useI18n();

const selectedOptions = computed<any[]>({
  get: () => {
    return klona(props.modelValue).sort((a, b) => a.order - b.order);
  },
  set: (value) => emit('update:modelValue', value),
});

const selectedIds = computed<any[]>({
  get: () => {
    if (!props.string) {
      // Return an array of ids based on the available options array
      return selectedOptions.value.map((option) => option[props.idKey]);
    }
    return selectedOptions.value;
  },
  set: (value) => {
    if (!props.string) {
      // Filter the available options by selected ids and set selectedOptions to the result
      selectedOptions.value = innerOptions.value!.filter((option) =>
        value.includes(option[props.idKey])
      );
    } else {
      selectedOptions.value = value;
    }
  },
});

const computedDisabled = computed(() => {
  if (props.disabled || isLoading.value) {
    return true;
  }

  return (
    !innerOptions.value?.length &&
    !props.autoCompleteCollection &&
    !selectedOptions.value.length &&
    !props.string
  );
});

const isDropDownArrowVisible = computed(
  () => innerOptions.value?.length || props.autoCompleteCollection
);

const fetchingOptions = ref(!!props.queryEndpoint);
const isLoading = computed(() => fetchingOptions.value || props.loading);

watch(isLoading, (newValue) => {
  if (!newValue) {
    showInput();
  }
});

// Query input

const inputRef = ref<typeof ComboboxInput>();

const inputVisible = ref(false);
const query = ref('');

const showInput = () => {
  if (!computedDisabled.value && !isLoading.value) {
    inputVisible.value = true;
    nextTick(() => {
      inputRef.value?.$el.focus();
    });
  }
};

const hasDivider = (index: number) => {
  if (props.hasDividerForLastOption) {
    return index === innerOptions.value.length - 2;
  }

  return (
    innerOptions.value.length > 1 && props.divideAfterIndexes.includes(index)
  );
};

const hideInput = (event: Event) => {
  let isChildOfCombobox = false;
  let currentNode = event.target;

  // event target is body when there is a layout shift in the element after clicking it
  // it's a race condition between click events causing this issue, see RCP-1323
  // the actual body should never be clicked given it's wrapped in the nuxt instance
  if ((event.target as HTMLElement)?.tagName === 'BODY') {
    return;
  }

  // Closing the combobox options before the v-model update was emitted cancels it
  while (currentNode !== null) {
    if (
      (currentNode as HTMLElement).id?.startsWith('headlessui-combobox-options')
    ) {
      isChildOfCombobox = true;
      break;
    }
    currentNode = (currentNode as HTMLElement).parentNode;
  }

  if (!isChildOfCombobox) {
    inputVisible.value = false;
    query.value = '';
  }
};

useOutsideClick(
  [inputRef.value?.$el],
  hideInput,
  computed(() => inputVisible.value),
  { forceOverridePreventDefault: true, usePointerDown: false }
);

// Fetching options

const hasVariableOptions = computed(
  () => props.string && !props.options.length
);

const hasFetchedAutoCompleteInitially = ref(false);

const isAutoCompleteSpinnerLoading = computed(() => {
  if (!hasFetchedAutoCompleteInitially.value && props.autoCompleteCollection) {
    return true;
  }

  return (
    props.autoCompleteCollection &&
    !innerOptions.value?.length &&
    isAutoCompleteLoading.value
  );
});

const isAutoCompleteLoading = ref(false);

const fetchAutoCompletion = debounce(
  (collection: DATABASE_AUTOCOMPLETE_COLLECTION) => {
    isAutoCompleteLoading.value = true;
    $client.search.autoComplete
      .query({
        collection,
        q: query.value.trim(),
        ...props.queryParams,
        preselectedIds: props.preselectedOptionIds,
      })
      .then((res) => {
        const selectedOptionsNotInResult = selectedOptions.value.filter(
          (option) => !res.items.some((item) => item._id === option._id)
        );

        const resFilteredOptions = props.blacklist
          ? res.items.filter(
            (item) => !props.blacklist?.some((bl) => bl._id === item?._id)
          )
          : res.items;

        innerOptions.value = [
          ...props.options.filter(
            (option) =>
              !selectedOptionsNotInResult.some(
                (optionNotInResult) => optionNotInResult._id === option._id
              )
          ),
          ...selectedOptionsNotInResult,
          ...resFilteredOptions,
        ];

        // initialize selected Options

        if (!hasFetchedAutoCompleteInitially.value) {
          if (
            (props.preselectedOptionIds?.length ||
              props.preselectedOptions?.length) &&
            !selectedOptions.value.length
          ) {
            if (!props.preselectedOptionIds?.length) {
              if (props.preselectedOptions) {
                selectedOptions.value = props.preselectedOptions;
              }
              return;
            }

            selectedOptions.value = [
              ...(props.preselectedOptions ?? []),
              ...getOptionsByPreselectedOptionIds(),
            ];
          }
        }
      })
      .catch((err) => {
        useErrorHandler(err, {
          type: 'error',
          message: err,
        });
      })
      .finally(() => {
        isAutoCompleteLoading.value = false;
        if (!hasFetchedAutoCompleteInitially.value) {
          hasFetchedAutoCompleteInitially.value = true;
        }
      });
  },
  300
);

const fetch = () => {
  if (!props.queryEndpoint || props.autoCompleteCollection) {
    return;
  }
  props
    .queryEndpoint(props.queryParams)
    .then(({ items }) => {
      innerOptions.value = items;
      fetchingOptions.value = false;
      emit('optionsFetched', items);

      if (props.queryParams) {
        // Verify the selected options in case we have query params
        // because certain selected options could be disappearing if the params change
        const itemIds = items.map((item: any) => item._id);
        const validatedOptions = selectedOptions.value.filter((option: any) =>
          itemIds.includes(props.string ? option : option[props.idKey])
        );

        // Only override selectedOptions if there is a difference
        if (!isEqual(selectedOptions.value, validatedOptions)) {
          selectedOptions.value = validatedOptions;
        }
      }
    })
    .catch((err) => {
      useErrorHandler(err, {
        type: 'error',
        message: err,
      });
    });
};

watch(query, () => {
  if (props.autoCompleteCollection) {
    // The reason for this hasFetchedAutoCompleteInitially equals false is that we have 300 miliseconds debounced
    // That mean it would show "No Options found" for 300 miliseconds
    hasFetchedAutoCompleteInitially.value = false;
    fetchAutoCompletion(props.autoCompleteCollection);
  }
});

watch(
  computed(() => props.queryParams),
  (newParams, oldParams) => {
    // We need !isEqual check here because for some reason, selecting options is triggering this watcher
    if (newParams && !isEqual(newParams, oldParams)) {
      if (props.autoCompleteCollection) {
        fetchAutoCompletion(props.autoCompleteCollection);
      } else if (props.queryEndpoint) {
        fetchingOptions.value = true;
        fetch();
      }
    }
  }
);

watch(
  computed(() => inputVisible.value),
  (newValue) => {
    if (!props.autoCompleteCollection || !newValue) {
      return;
    }
    fetchAutoCompletion(props.autoCompleteCollection);
  }
);

watch(
  computed(() => props.preselectedOptionIds),
  (newValue) => {
    if (newValue && props.autoCompleteCollection) {
      fetchAutoCompletion(props.autoCompleteCollection);
    }
  },
  { immediate: true }
);

fetch();

// Inner options

const innerOptions = ref(props.options);

const removeOption = (index: number) => {
  if (props.required && selectedOptions.value.length <= 1 && props.async) {
    notificationsStore.createNotification({
      type: 'error',
      title: props.label,
      message: i18n.t('error-messages.is-required'),
    });
  } else {
    const splicedOptions = selectedOptions.value;
    splicedOptions.splice(index, 1);
    selectedOptions.value = splicedOptions;
  }
};

const handleInputByKeyUpEvent = () => {
  /* We are using <Combobox> by Headless UI
  / https://headlessui.com/v1/vue/combobox
  <ComboboxInput> has it's own logic for the Keyup Event.
  If <ComboboxOptions> has any options, than on "keyUp" event
  the option will be added to selectedOptions automatically or unselected (toggled).
  If there is NO options to select from, ComboboxInput doesn't handle the keyUp Event.
  In this case, we have to handle this manually.
  Our tags input is used on our app in two different variations:
  With and without options to select from.
  */

  if (filteredOptions.value?.length) {
    // In this case, adding or removing option is handled by <Combobox> component already
    // Only clearing the Input value is not handled by the Combobox Component
    clearInputValue();
  } else if (hasVariableOptions.value) {
    addOption();
  } else {
    // Input value will be reset by headless-ui, we only need to reset
    // the `query` of our component state so the options are shown again in the dropdown.
    clearInputValue();
  }
};

const clearInputValue = () => {
  query.value = '';
  if (inputRef.value?.$el) {
    inputRef.value.$el.value = '';
    inputRef.value?.$el.focus();
  }
};

const addOption = () => {
  if (props.string && query.value.trim()) {
    const isOptionAvailable = selectedOptions.value.some(
      (option) =>
        option.trim().toLowerCase() === query.value.trim().toLowerCase()
    );
    if (isOptionAvailable) {
      notificationsStore.createNotification({
        type: 'error',
        title: i18n.t('error-messages.can-not-add-option'),
        message: i18n.t('error-messages.option-exists'),
      });
    } else {
      selectedOptions.value = [...selectedOptions.value, query.value.trim()];
    }
  }
  clearInputValue();
};

const filteredOptions = computed<any[]>(() => {
  if (innerOptions.value) {
    return query.value.trim().length === 0
      ? innerOptions.value
      : innerOptions.value.filter((option: any) =>
        (props.string ? option : option[props.filterKey])
          .toLowerCase()
          .includes(query.value.trim().toLowerCase())
      );
  }
  return [];
});

const getOptionsByPreselectedOptionIds = () => {
  if (!props.preselectedOptionIds) {
    return [];
  }
  return props.preselectedOptionIds
    .map((optionId) =>
      innerOptions.value?.find((option) => {
        return option[props.idKey].toString() === optionId.toString();
      })
    )
    .filter(Boolean);
};

watch(
  computed(() => props.options),
  (newOptions) => {
    innerOptions.value = newOptions;

    if (props.preselectedOptionIds) {
      if (!selectedOptions.value.length) {
        selectedOptions.value = getOptionsByPreselectedOptionIds();
      }
    }
  },
  { immediate: true }
);

// Placeholder

const computedPlaceholder = computed(() => {
  if (isLoading.value) {
    return i18n.t('general.loading');
  }

  if (
    !innerOptions.value?.length &&
    !props.string &&
    !props.autoCompleteCollection
  ) {
    return i18n.t('general.no-options-available');
  }

  if (inputVisible.value) {
    return props.placeholder;
  }

  if (props.async) {
    return selectedOptions.value.length
      ? props.placeholder
      : i18n.t('general.none');
  }

  return props.placeholder;
});

const comboboxOptionsPositionRef = ref();
const comboboxOptionsRef = ref();
const stopAutoUpdatePlacement = ref<null | (() => void)>(null);

watch(inputVisible, async (newValue) => {
  if (!newValue) {
    return;
  }
  if (stopAutoUpdatePlacement.value) {
    stopAutoUpdatePlacement.value();
  }
  stopAutoUpdatePlacement.value = (
    await useSmartPositioning(comboboxOptionsPositionRef, comboboxOptionsRef, {
      setMaxWidth: true,
      computeOptions: {
        placement: 'bottom',
        middleware: [
          offset(({ placement }) => {
            return placement.startsWith('top') ? 20 : 4;
          }),
          flip(),
        ],
      },
    }).getSmartPositionWithAutoUpdate()
  ).value;
});

defineExpose({ comboboxOptionsRef });
</script>
